/**
 * <biosphere-tabs> — enhancement for the tablist + panels rendered by
 * snippets/biosphere-tabs.liquid.
 *
 * The no-JS baseline is all panels stacked with the tablist hidden. On
 * connect, panels get their tabpanel wiring, all but the selected panel
 * are hidden, and the tablist is revealed. Selection follows focus
 * (APG-style roving tabindex with arrow/Home/End keys).
 */
class BiosphereTabs extends HTMLElement {
  #tablist;
  #tabs = [];
  #panels = [];
  #index = 0;
  #abort;

  connectedCallback() {
    this.#tablist = this.querySelector('[role="tablist"]');
    this.#tabs = [...this.querySelectorAll('[role="tab"]')];
    const container = this.querySelector(".biosphere-tabs__panels");
    this.#panels = container ? [...container.children] : [];

    // A title without a panel (or vice versa) means the caller's contract
    // is broken; leave the readable stacked baseline in place.
    const paired =
      this.#tabs.length > 1 && this.#tabs.length === this.#panels.length;
    if (!this.#tablist || !paired) return;

    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.#panels.forEach((panel, i) => {
      const tab = this.#tabs[i];
      panel.id = panel.id || `${tab.id}-panel`;
      panel.setAttribute("role", "tabpanel");
      panel.setAttribute("aria-labelledby", tab.id);
      panel.tabIndex = 0;
      tab.setAttribute("aria-controls", panel.id);
      tab.addEventListener("click", () => this.#select(i), { signal });
    });

    this.#tablist.addEventListener(
      "keydown",
      (event) => this.#onKeydown(event),
      { signal },
    );

    this.#apply();
    this.#tablist.hidden = false;
  }

  disconnectedCallback() {
    this.#abort?.abort();
  }

  #onKeydown(event) {
    // Arrows follow the inline direction, so they flip in RTL.
    const flip = getComputedStyle(this.#tablist).direction === "rtl" ? -1 : 1;
    const last = this.#tabs.length - 1;
    let target;

    if (event.key === "ArrowRight") target = this.#index + flip;
    else if (event.key === "ArrowLeft") target = this.#index - flip;
    else if (event.key === "Home") target = 0;
    else if (event.key === "End") target = last;
    else return;

    event.preventDefault();
    const index = (target + this.#tabs.length) % this.#tabs.length;
    this.#select(index);
    this.#tabs[index].focus();
  }

  #select(index) {
    if (index === this.#index) return;
    this.#index = index;
    this.#apply();
    this.dispatchEvent(
      new CustomEvent("biosphere:tabs:change", {
        bubbles: true,
        detail: { index, total: this.#tabs.length },
      }),
    );
  }

  #apply() {
    // Every tab in the tab order, where the caller has asked for it: its
    // tabs are the content rather than labels over it, and a reader who
    // only presses Tab would otherwise reach one of them and no more.
    const roving = !this.hasAttribute("data-all-tabbable");

    this.#tabs.forEach((tab, i) => {
      const selected = i === this.#index;
      tab.setAttribute("aria-selected", String(selected));
      tab.tabIndex = selected || !roving ? 0 : -1;
      this.#panels[i].hidden = !selected;
    });
  }
}

if (!customElements.get("biosphere-tabs")) {
  customElements.define("biosphere-tabs", BiosphereTabs);
}
